import { useEffect, useRef, useState } from 'react';

const visibilityCallbacks = new Map<Element, () => void>();
let visibilityObserver: IntersectionObserver | null = null;

const stopObserving = (element: Element) => {
  visibilityObserver?.unobserve(element);
  visibilityCallbacks.delete(element);

  if (visibilityCallbacks.size === 0) {
    visibilityObserver?.disconnect();
    visibilityObserver = null;
  }
};

const observeNearViewport = (element: Element, onVisible: () => void) => {
  visibilityObserver ??= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const callback = visibilityCallbacks.get(entry.target);
        stopObserving(entry.target);
        callback?.();
      });
    },
    { rootMargin: '200px' }
  );

  visibilityCallbacks.set(element, onVisible);
  visibilityObserver.observe(element);

  return () => stopObserving(element);
};

export const useNearViewport = <T extends Element>(enabled: boolean) => {
  const elementRef = useRef<T | null>(null);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  const supportsIntersectionObserver = typeof IntersectionObserver !== 'undefined';

  useEffect(() => {
    if (!enabled || !supportsIntersectionObserver || hasEnteredViewport || !elementRef.current) {
      return;
    }

    return observeNearViewport(elementRef.current, () => setHasEnteredViewport(true));
  }, [enabled, hasEnteredViewport, supportsIntersectionObserver]);

  return {
    elementRef,
    isNearViewport: !enabled || !supportsIntersectionObserver || hasEnteredViewport,
  };
};
